// Copyright © 2019-2026 Focal Systems. All rights reserved.

import {
  Building2,
  Cpu,
  DatabaseZap,
  Fingerprint,
  IdCard,
  MemoryStick,
  RulerDimensionLine,
  Settings,
  ShieldCheck,
  Store,
  UserRound,
  UsersRound,
} from "lucide-react";
import type * as React from "react";
import { ImportIcon, Sushi2Icon } from "@/components/icons";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export type NavItem = {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  labelKey: string;
  /** Permission required to view this section. */
  permission: string;
  /** Only show when debug mode is enabled (pages under construction) */
  debugOnly?: boolean;
};

export type NavGroup = {
  labelKey: string;
  items: readonly NavItem[];
};

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    labelKey: "nav.management",
    items: [
      { to: "/chains", icon: Building2, labelKey: "chains.title", permission: PAGE_PERMISSIONS.chains },
      { to: "/stores", icon: Store, labelKey: "stores.title", permission: PAGE_PERMISSIONS.stores },
      { to: "/settings", icon: Settings, labelKey: "settings.title", permission: PAGE_PERMISSIONS.settings },
    ],
  },
  {
    labelKey: "nav.iam",
    items: [
      { to: "/iam/users", icon: UserRound, labelKey: "users.title", permission: PAGE_PERMISSIONS.iamUsers },
      {
        to: "/iam/groups",
        icon: UsersRound,
        labelKey: "groups.title",
        permission: PAGE_PERMISSIONS.iamGroups,
      },
      {
        to: "/iam/permissions",
        icon: ShieldCheck,
        labelKey: "permissions.title",
        permission: PAGE_PERMISSIONS.iamPermissions,
        debugOnly: true,
      },
      {
        to: "/iam/sso",
        icon: Fingerprint,
        labelKey: "sso.title",
        permission: PAGE_PERMISSIONS.iamSso,
        debugOnly: true,
      },
      {
        to: "/iam/vendors",
        icon: IdCard,
        labelKey: "vendors.title",
        permission: PAGE_PERMISSIONS.iamVendors,
      },
    ],
  },
  {
    labelKey: "nav.ingress",
    items: [
      {
        to: "/ingress/imports",
        icon: ImportIcon,
        labelKey: "ingressImports.title",
        permission: PAGE_PERMISSIONS.ingressImports,
      },
      {
        to: "/ingress/transformations",
        icon: DatabaseZap,
        labelKey: "transformations.title",
        permission: PAGE_PERMISSIONS.ingressTransformations,
      },
    ],
  },
  {
    labelKey: "nav.octo",
    items: [
      {
        to: "/octo/rules",
        icon: Sushi2Icon,
        labelKey: "rules.title",
        permission: PAGE_PERMISSIONS.octoRules,
      },
      {
        to: "/octo/visualizer",
        icon: RulerDimensionLine,
        labelKey: "visualizer.title",
        permission: PAGE_PERMISSIONS.octoVisualizer,
        debugOnly: true,
      },
    ],
  },
  {
    labelKey: "nav.hardware",
    items: [
      {
        to: "/hardware/devices",
        icon: Cpu,
        labelKey: "devices.title",
        permission: PAGE_PERMISSIONS.hardwareDevices,
      },
      {
        to: "/hardware/firmware",
        icon: MemoryStick,
        labelKey: "firmware.title",
        permission: PAGE_PERMISSIONS.hardwareFirmware,
      },
    ],
  },
] as const;

/** Flat list of all nav items (for command palette, etc.) */
export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => g.items);
